import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Converts a string into a URL-friendly slug for real-time input sanitization.
// More aggressive than the backend's normalize_url (which only downcases/strips)
// because this runs on every keystroke. The backend validates the final format.
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-/, "")
}

export function formatCents(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`
  if (dollars === Math.floor(dollars)) return `$${dollars.toFixed(0)}`
  return `$${dollars.toFixed(2)}`
}

export function formatDurationHuman(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  if (mins < 1) return "< 1 min"
  return `${mins} min`
}
const megabyteFormatter = new Intl.NumberFormat("en", {
  style: "unit",
  unit: "megabyte",
  maximumFractionDigits: 1,
})

export function formatMegabytes(bytes: number): string {
  return megabyteFormatter.format(bytes / 1_000_000)
}

export function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
) {
  return `${count} ${count === 1 ? singular : plural}`
}

// Memoized once at module load rather than constructed on every call — the
// >1-week fallback in formatRelativeTime formats with these options.
const shortDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
})

export function formatRelativeTime(isoTime: string): string {
  const diffSeconds = Math.floor(
    (Date.now() - new Date(isoTime).getTime()) / 1000,
  )
  if (diffSeconds < 60) return "just now"
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`
  if (diffSeconds < 604800) {
    const days = Math.floor(diffSeconds / 86400)
    return days === 1 ? "yesterday" : `${days}d ago`
  }
  return shortDateFormatter.format(new Date(isoTime))
}

// "M:SS" for media player timers and duration chips (e.g. 0 -> "0:00",
// 195 -> "3:15"). Floors seconds and clamps non-finite/negative input to 0, so
// live timers fed raw audio.currentTime never render "NaN:NaN" or a rounded "1:60".
export function formatTime(seconds: number): string {
  // Guard clause for every caller: NaN/Infinity (unloaded or live media
  // durations) and negatives all render as the zero clock.
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00"

  const total = Math.floor(seconds)
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

export const formatClock = formatTime

const orNullWhenEmpty =
  (format: (seconds: number) => string) =>
  (seconds: number | null | undefined): string | null =>
    !seconds || seconds <= 0 ? null : format(seconds)

export const formatDuration = orNullWhenEmpty(formatTime)

// The words inside editor markup, near enough to what the server sends to a
// mail. Anything counted or capped against what a reader reads (the welcome
// email's 1,000 characters) measures this, not the tags around it.
export function richTextPlainText(html: string | null | undefined): string {
  if (!html) return ""
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, "\n\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&amp;/gi, "&")
    .trim()
}

export function isRichTextBlank(html: string | null | undefined): boolean {
  if (!html) return true
  if (/<(img|figure|action-text-attachment|iframe|video|audio)\b/i.test(html)) {
    return false
  }
  return richTextPlainText(html).replace(/\s+/g, "").length === 0
}

// Escape a string for interpolation into HTML markup we hand to a human to
// paste elsewhere (embed snippets, copied iframes). Same character set as
// Rails' ERB::Util.html_escape, so a snippet built here matches one built
// server-side character for character.
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

// Rails CSRF token from the document head, for non-Inertia fetches and for
// hand-built form posts.
export function csrfToken(): string {
  const meta = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")
  return meta?.content ?? ""
}

// Did a Rails write actually land? `ok` alone is not enough: an expired session
// makes `authenticate_user!` send a 302 to the sign-in page, which is followed
// to a 200 HTML page, so a caller trusting `ok` would report a write that never
// happened. A write answered by a redirect is a write that did not happen.
export function wroteSuccessfully(response: {
  ok: boolean
  redirected: boolean
}): boolean {
  return response.ok && !response.redirected
}

// Debounce utility: returns a debounced version of the given function
export function debounce<T extends (...args: unknown[]) => void>(
  fn: (this: void, ...args: unknown[]) => void,
  delay: number,
) {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), delay)
  }
}

// Rails returns error arrays while Inertia types them as strings. Accept both
// so creators see the server's upload rejection (#1747).
export function errorMessageFor(
  errors: unknown,
  key: string,
): string | undefined {
  if (typeof errors !== "object" || errors === null) return undefined
  const value = (errors as Record<string, unknown>)[key]
  if (typeof value === "string") return value
  if (Array.isArray(value) && typeof value[0] === "string") return value[0]
  return undefined
}

// Content of a <meta name="..."> the server rendered into the head, or null
// when it is absent (or there is no document, as under SSR).
export function metaContent(name: string): string | null {
  if (typeof document === "undefined") return null

  return (
    document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") ??
    null
  )
}

// Environment tag for the analytics and error-reporting SDKs, so both file
// events under the same name. The server renders Rails.env into the head
// (shared/_observability_meta), so this matches development, staging,
// production exactly instead of guessing from the hostname. A document without
// that partial files under "unknown": loud and never wrong, where a guess
// (every deployed build is a production-mode Vite build, staging included)
// would silently file staging events as production.
export function currentEnvironment(): string {
  return metaContent("app-environment") ?? "unknown"
}

const CONTINUOUS_PLAY_KEY = "indee:container:autoplay"

export function continuousPlayEnabled(): boolean {
  try {
    return localStorage.getItem(CONTINUOUS_PLAY_KEY) === "true"
  } catch {
    return false
  }
}

export function setContinuousPlay(enabled: boolean): boolean {
  try {
    localStorage.setItem(CONTINUOUS_PLAY_KEY, String(enabled))
    return true
  } catch {
    return false
  }
}
